
import React from 'react';
import { Sliders, Calendar, Heart, Clock, Moon, Droplets } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для быстрой настройки параметров цикла
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  calculateFertilityWindow,
  setResults,
  generateCalendarData
}) => {
  // Функция для быстрой установки длительности цикла
  const setCycleLength = (length) => {
    setFormData(prev => ({
      ...prev,
      cycleLength: length.toString()
    }));
  };

  // Функция для быстрой установки длительности лютеиновой фазы
  const setLutealPhaseLength = (length) => {
    setFormData(prev => ({
      ...prev,
      lutealPhaseLength: length.toString()
    }));
  };

  // Функция для быстрой установки длительности менструации
  const setPeriodLength = (length) => {
    setFormData(prev => ({
      ...prev,
      periodLength: length.toString()
    }));
  };

  // Функция для быстрых расчетов
  const handleQuickCalculate = () => {
    // Проверяем, что у нас есть все необходимые данные
    if (!formData.lastPeriodDate || !formData.cycleLength || !formData.lutealPhaseLength) {
      return;
    }

    // Расчет и установка результатов
    const results = calculateFertilityWindow(
      formData.lastPeriodDate,
      parseInt(formData.cycleLength, 10),
      parseInt(formData.lutealPhaseLength, 10)
    );

    // Обновляем результаты и генерируем данные календаря
    setResults({
      ...results,
      cycleLength: parseInt(formData.cycleLength, 10),
      periodLength: parseInt(formData.periodLength, 10),
      lutealPhaseLength: parseInt(formData.lutealPhaseLength, 10),
      calculatedAt: new Date()
    });
    
    generateCalendarData(results);
  };

  // Предопределенные длительности цикла
  const predefinedCycleLengths = [
    { value: '21', label: '21 день', description: 'Короткий' },
    { value: '28', label: '28 дней', description: 'Стандартный' },
    { value: '30', label: '30 дней', description: 'Распространенный' },
    { value: '35', label: '35 дней', description: 'Длинный' }
  ];

  // Предопределенные длительности лютеиновой фазы
  const predefinedLutealLengths = [
    { value: '12', label: '12 дней' },
    { value: '14', label: '14 дней' },
    { value: '16', label: '16 дней' }
  ];

  // Предопределенные длительности менструации
  const predefinedPeriodLengths = [
    { value: '3', label: '3 дня' },
    { value: '5', label: '5 дней' },
    { value: '7', label: '7 дней' }
  ];

  // Функция для создания выбора даты последней менструации
  const handleQuickDateSelect = (daysDelta) => {
    const date = new Date();
    date.setDate(date.getDate() + daysDelta);
    setFormData(prev => ({
      ...prev,
      lastPeriodDate: date.toISOString().split('T')[0]
    }));
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Sliders className="w-5 h-5 mr-2 text-rose-500" />
        Быстрая настройка
      </h3>
      
      <div className="space-y-5">
        {/* Быстрая установка даты последней менструации */}
        <div>
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
            <Calendar className="w-4 h-4 mr-2 text-rose-500" />
            Последняя менструация:
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Button
              type="button"
              onClick={() => handleQuickDateSelect(0)}
              variant="outline"
              size="sm"
              className={formData.lastPeriodDate === new Date().toISOString().split('T')[0] ? 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800' : ''}
            >
              Сегодня
            </Button>
            <Button
              type="button"
              onClick={() => handleQuickDateSelect(-7)}
              variant="outline"
              size="sm"
              className={new Date(formData.lastPeriodDate).toDateString() === new Date(new Date().setDate(new Date().getDate() - 7)).toDateString() ? 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800' : ''}
            >
              7 дней назад
            </Button>
            <Button
              type="button"
              onClick={() => handleQuickDateSelect(-14)}
              variant="outline"
              size="sm"
              className={new Date(formData.lastPeriodDate).toDateString() === new Date(new Date().setDate(new Date().getDate() - 14)).toDateString() ? 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800' : ''}
            >
              14 дней назад
            </Button>
          </div>
        </div>
        
        {/* Длительность цикла */}
        <div>
          <div className="flex justify-between text-sm mb-2">
            <label className="font-medium text-gray-700 dark:text-gray-300 flex items-center">
              <Clock className="w-4 h-4 mr-2 text-rose-500" />
              Длительность цикла:
            </label>
            <span className="font-medium text-rose-600 dark:text-rose-400">{formData.cycleLength} дней</span>
          </div>
          <input
            type="range"
            min="21"
            max="45"
            step="1"
            value={formData.cycleLength}
            onChange={(e) => setCycleLength(e.target.value)}
            aria-label="Длительность цикла"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-rose-600"
          />
          
          {/* Кнопки с предопределенными длительностями цикла */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedCycleLengths.map(length => (
              <button
                key={length.value}
                type="button"
                onClick={() => setCycleLength(length.value)}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.cycleLength === length.value 
                    ? 'bg-rose-100 border-rose-300 text-rose-700 dark:bg-rose-900/30 dark:border-rose-700 dark:text-rose-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                title={length.description}
              >
                {length.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Длительность лютеиновой фазы */}
        <div>
          <div className="flex justify-between text-sm mb-2">
            <label className="font-medium text-gray-700 dark:text-gray-300 flex items-center">
              <Moon className="w-4 h-4 mr-2 text-rose-500" />
              Лютеиновая фаза:
            </label>
            <span className="font-medium text-rose-600 dark:text-rose-400">{formData.lutealPhaseLength} дней</span>
          </div>
          <div className="grid grid-cols-3 gap-1">
            {predefinedLutealLengths.map(length => (
              <button
                key={length.value}
                type="button"
                onClick={() => setLutealPhaseLength(length.value)}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.lutealPhaseLength === length.value 
                    ? 'bg-rose-100 border-rose-300 text-rose-700 dark:bg-rose-900/30 dark:border-rose-700 dark:text-rose-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {length.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Длительность менструации */}
        <div>
          <div className="flex justify-between text-sm mb-2">
            <label className="font-medium text-gray-700 dark:text-gray-300 flex items-center">
              <Droplets className="w-4 h-4 mr-2 text-rose-500" />
              Длительность менструации:
            </label>
            <span className="font-medium text-rose-600 dark:text-rose-400">{formData.periodLength} дней</span>
          </div>
          <div className="grid grid-cols-3 gap-1">
            {predefinedPeriodLengths.map(length => (
              <button
                key={length.value}
                type="button"
                onClick={() => setPeriodLength(length.value)}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.periodLength === length.value 
                    ? 'bg-rose-100 border-rose-300 text-rose-700 dark:bg-rose-900/30 dark:border-rose-700 dark:text-rose-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {length.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Кнопка быстрого расчета */}
        <Button
          primary
          fullWidth
          onClick={handleQuickCalculate}
          icon={<Heart className="w-4 h-4" />}
          disabled={isCalculating || !formData.lastPeriodDate || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать овуляцию
        </Button>

        {/* Подсказка о персонализированной настройке */}
        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-100 dark:border-blue-800">
          <p className="text-xs text-blue-700 dark:text-blue-400">
            <strong>Точность расчета:</strong> Для более точных результатов используйте реальные данные о вашем цикле. Стандартные значения могут не подходить для вашего индивидуального случая.
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;